
// src/pages/VisitReport/VisitReportDashboardPage.js
// Admin / Marketing Manager: what every manager & executive collected.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ListChecks } from 'lucide-react';
import { selectAuthState } from '../../store/selectors/authSelector';
import { fetchExecutiveSummary } from '../../services/features/visitReport/visitReportSlice';
import RangeBar from '../../components/Report/RangeBar';
import {
  computeRange,
  formatDateTime,
  FLOW_META,
} from '../../utils/attendanceFormat';
import '../Shared/ReportPages.css';

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

const ROLE_FILTERS = [
  { key: '', label: 'All' },
  { key: 'MarketingManager', label: 'Managers' },
  { key: 'MarketingExecutive', label: 'Executives' },
];

const ROLE_LABEL = {
  MarketingManager: 'Marketing Manager',
  MarketingExecutive: 'Marketing Executive',
  Distributor: 'Distributor',
  FSE: 'FSE',
};

const PAGE_SIZE = 12;

const Pill = ({ flow, value }) => {
  const m = FLOW_META[flow];
  return (
    <span className="rp-pill" style={{ background: m.bg, color: m.color }}>
      {m.label} {value}
    </span>
  );
};

const VisitReportDashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role } = useSelector(selectAuthState);
  const { execSummary, summaryLoading } = useSelector((s) => s.visitReport);

  // Default 7 days → data is visible immediately, nothing to type.
  const [range, setRange] = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { from, to } = useMemo(
    () => computeRange(range, customFrom, customTo),
    [range, customFrom, customTo]
  );
  const rangeLabel = PRESETS.find((p) => p.key === range)?.label || '';

  const load = useCallback(() => {
    dispatch(fetchExecutiveSummary({ from: from || undefined, to: to || undefined }));
  }, [dispatch, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [roleFilter, search, range]);

  const allPeople = execSummary?.people || [];
  const rolePeople = useMemo(
    () => (roleFilter ? allPeople.filter((p) => p.role === roleFilter) : allPeople),
    [allPeople, roleFilter]
  );

  const kpi = useMemo(
    () =>
      rolePeople.reduce(
        (t, p) => ({
          reports: t.reports + p.total,
          hot: t.hot + p.hot,
          warm: t.warm + p.warm,
          cold: t.cold + p.cold,
          active: t.active + (p.total > 0 ? 1 : 0),
        }),
        { reports: 0, hot: 0, warm: 0, cold: 0, active: 0 }
      ),
    [rolePeople]
  );
  const inactive = rolePeople.length - kpi.active;

  const people = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rolePeople;
    return rolePeople.filter(
      (p) =>
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.mobile || '').includes(q) ||
        String(p.district || '').toLowerCase().includes(q)
    );
  }, [rolePeople, search]);

  const totalPages = Math.max(1, Math.ceil(people.length / PAGE_SIZE));
  const pageRows = people.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const qs = (extra = {}) => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    p.set('label', rangeLabel);
    Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  };

  const openPerson = (p) =>
    navigate(`/visit-reports/list?${qs({ executiveId: String(p.executiveId), name: p.name })}`);

  const kpis = [
    { label: 'Total Reports', value: kpi.reports, color: 'var(--text-primary)' },
    { label: 'Hot', value: kpi.hot, color: FLOW_META.HOT.color },
    { label: 'Warm', value: kpi.warm, color: FLOW_META.WARM.color },
    { label: 'Cold', value: kpi.cold, color: FLOW_META.COLD.color },
    { label: 'Submitted', value: kpi.active, color: '#10b981' },
    { label: 'Not Submitted', value: inactive, color: inactive > 0 ? '#ef4444' : 'var(--text-muted)' },
  ];

  return (
    <div className="rp-page">
      {/* Filters */}
      <div className="rp-toolbar">
        <RangeBar
          presets={PRESETS}
          range={range}
          onRange={setRange}
          from={customFrom}
          to={customTo}
          onFrom={setCustomFrom}
          onTo={setCustomTo}
        />
        <div className="rp-spacer" />
        <button className="rp-chip" onClick={() => navigate(`/visit-reports/list?${qs()}`)}>
          <ListChecks size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          All Reports
        </button>
        {role === 'MarketingManager' && (
          <button className="rp-chip active" onClick={() => navigate('/visit-reports/new')}>
            <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            New Report
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="rp-kpis">
        {kpis.map((k) => (
          <div key={k.label} className="rp-kpi" style={{ '--kpi': k.color }}>
            <div className="rp-kpi-value">{k.value}</div>
            <div className="rp-kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rp-card">
        <div className="rp-toolbar" style={{ padding: '14px 16px' }}>
          <div className="rp-chips">
            {ROLE_FILTERS.map((r) => (
              <button
                key={r.key || 'all'}
                className={`rp-chip ${roleFilter === r.key ? 'active' : ''}`}
                onClick={() => setRoleFilter(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="rp-spacer" />
          <div className="rp-search">
            <Search size={14} />
            <input
              className="rp-input"
              placeholder="Search name, mobile, district…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {summaryLoading && !execSummary ? (
          <div className="rp-loading">Loading…</div>
        ) : people.length === 0 ? (
          <div className="rp-empty">
            <b>No managers or executives found</b>
            Try another date range or clear the search.
          </div>
        ) : (
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th className="rp-num">Reports</th>
                  <th>Lead split</th>
                  <th className="rp-num">Customers</th>
                  <th>Last report</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((p) => {
                  const empty = p.total === 0;
                  return (
                    <tr
                      key={String(p.executiveId)}
                      className="clickable"
                      style={empty ? { opacity: 0.7 } : undefined}
                      onClick={() => openPerson(p)}
                    >
                      <td>
                        <div className="rp-person">
                          <div className="rp-avatar">{(p.name || '?').charAt(0).toUpperCase()}</div>
                          <div style={{ minWidth: 0 }}>
                            <div className="rp-strong">{p.name || '—'}</div>
                            <div className="rp-sub">
                              {ROLE_LABEL[p.role] || p.role}
                              {p.district ? ` · ${p.district}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="rp-num rp-strong" style={{ fontSize: 18 }}>{p.total}</td>
                      <td style={{ minWidth: 200 }}>
                        <div className="rp-bar">
                          {empty ? (
                            <div style={{ flex: 1 }} />
                          ) : (
                            <>
                              {p.hot > 0 && <div style={{ flex: p.hot, background: FLOW_META.HOT.color }} />}
                              {p.warm > 0 && <div style={{ flex: p.warm, background: FLOW_META.WARM.color }} />}
                              {p.cold > 0 && <div style={{ flex: p.cold, background: FLOW_META.COLD.color }} />}
                            </>
                          )}
                        </div>
                        <div className="rp-pills" style={{ marginTop: 8 }}>
                          <Pill flow="HOT" value={p.hot} />
                          <Pill flow="WARM" value={p.warm} />
                          <Pill flow="COLD" value={p.cold} />
                        </div>
                      </td>
                      <td className="rp-num">{p.uniqueCustomers}</td>
                      <td className={empty ? 'rp-muted' : ''}>
                        {empty ? `No reports (${rangeLabel.toLowerCase()})` : formatDateTime(p.lastSubmittedAt)}
                      </td>
                      <td className="rp-num">
                        <button className="rp-link">View →</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="rp-pager">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, people.length)} of {people.length}
            </span>
            <div className="rp-pager-btns">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
              <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitReportDashboardPage;


//----------------- 13.10.2026 Backup ---------------------
// // src/pages/VisitReport/VisitReportDashboardPage.js
// // Admin / Marketing Manager: what every manager & executive collected.
// import React, { useCallback, useEffect, useMemo, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { Search, Plus, ListChecks } from 'lucide-react';
// import { selectAuthState } from '../../store/selectors/authSelector';
// import { fetchExecutiveSummary } from '../../services/features/visitReport/visitReportSlice';
// import RangeBar from '../../components/Report/RangeBar';
// import {
//   computeRange,
//   formatDateTime,
//   FLOW_META,
// } from '../../utils/attendanceFormat';
// import '../Shared/ReportPages.css';

// const PRESETS = [
//   { key: 'today', label: 'Today' },
//   { key: 'week', label: '7 Days' },
//   { key: 'month', label: 'This Month' },
//   { key: 'all', label: 'All Time' },
//   { key: 'custom', label: 'Custom' },
// ];

// const ROLE_FILTERS = [
//   { key: '', label: 'All' },
//   { key: 'MarketingManager', label: 'Managers' },
//   { key: 'MarketingExecutive', label: 'Executives' },
// ];

// const ROLE_LABEL = {
//   MarketingManager: 'Marketing Manager',
//   MarketingExecutive: 'Marketing Executive',
//   Distributor: 'Distributor',
//   FSE: 'FSE',
// };

// const PAGE_SIZE = 12;

// const Pill = ({ flow, value }) => {
//   const m = FLOW_META[flow];
//   return (
//     <span className="rp-pill" style={{ background: m.bg, color: m.color }}>
//       {m.label} {value}
//     </span>
//   );
// };

// const VisitReportDashboardPage = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { role } = useSelector(selectAuthState);
//   const { execSummary, summaryLoading } = useSelector((s) => s.visitReport);

//   // Default 7 days → data is visible immediately, nothing to type.
//   const [range, setRange] = useState('week');
//   const [customFrom, setCustomFrom] = useState('');
//   const [customTo, setCustomTo] = useState('');
//   const [roleFilter, setRoleFilter] = useState('');
//   const [search, setSearch] = useState('');
//   const [page, setPage] = useState(1);

//   const { from, to } = useMemo(
//     () => computeRange(range, customFrom, customTo),
//     [range, customFrom, customTo]
//   );
//   const rangeLabel = PRESETS.find((p) => p.key === range)?.label || '';

//   const load = useCallback(() => {
//     dispatch(fetchExecutiveSummary({ from: from || undefined, to: to || undefined }));
//   }, [dispatch, from, to]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   useEffect(() => setPage(1), [roleFilter, search, range]);

//   const allPeople = execSummary?.people || [];
//   const rolePeople = useMemo(
//     () => (roleFilter ? allPeople.filter((p) => p.role === roleFilter) : allPeople),
//     [allPeople, roleFilter]
//   );

//   const kpi = useMemo(
//     () =>
//       rolePeople.reduce(
//         (t, p) => ({
//           reports: t.reports + p.total,
//           hot: t.hot + p.hot,
//           warm: t.warm + p.warm,
//           cold: t.cold + p.cold,
//           active: t.active + (p.total > 0 ? 1 : 0),
//         }),
//         { reports: 0, hot: 0, warm: 0, cold: 0, active: 0 }
//       ),
//     [rolePeople]
//   );
//   const inactive = rolePeople.length - kpi.active;

//   const people = useMemo(() => {
//     const q = search.trim().toLowerCase();
//     if (!q) return rolePeople;
//     return rolePeople.filter(
//       (p) =>
//         String(p.name || '').toLowerCase().includes(q) ||
//         String(p.mobile || '').includes(q) ||
//         String(p.district || '').toLowerCase().includes(q)
//     );
//   }, [rolePeople, search]);

//   const totalPages = Math.max(1, Math.ceil(people.length / PAGE_SIZE));
//   const pageRows = people.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

//   const qs = (extra = {}) => {
//     const p = new URLSearchParams();
//     if (from) p.set('from', from);
//     if (to) p.set('to', to);
//     p.set('label', rangeLabel);
//     Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
//     return p.toString();
//   };

//   const openPerson = (p) =>
//     navigate(`/visit-reports/list?${qs({ executiveId: String(p.executiveId), name: p.name })}`);

//   const kpis = [
//     { label: 'Total Reports', value: kpi.reports, color: 'var(--text-primary)' },
//     { label: 'Hot', value: kpi.hot, color: FLOW_META.HOT.color },
//     { label: 'Warm', value: kpi.warm, color: FLOW_META.WARM.color },
//     { label: 'Cold', value: kpi.cold, color: FLOW_META.COLD.color },
//     { label: 'Submitted', value: kpi.active, color: '#10b981' },
//     { label: 'Not Submitted', value: inactive, color: inactive > 0 ? '#ef4444' : 'var(--text-muted)' },
//   ];

//   return (
//     <div className="rp-page">
//       {/* Filters */}
//       <div className="rp-toolbar">
//         <RangeBar
//           presets={PRESETS}
//           range={range}
//           onRange={setRange}
//           from={customFrom}
//           to={customTo}
//           onFrom={setCustomFrom}
//           onTo={setCustomTo}
//         />
//         <div className="rp-spacer" />
//         <button className="rp-chip" onClick={() => navigate(`/visit-reports/list?${qs()}`)}>
//           <ListChecks size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
//           All Reports
//         </button>
//         {role === 'MarketingManager' && (
//           <button className="rp-chip active" onClick={() => navigate('/visit-reports/new')}>
//             <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
//             New Report
//           </button>
//         )}
//       </div>

//       {/* KPIs */}
//       <div className="rp-kpis">
//         {kpis.map((k) => (
//           <div key={k.label} className="rp-kpi" style={{ '--kpi': k.color }}>
//             <div className="rp-kpi-value">{k.value}</div>
//             <div className="rp-kpi-label">{k.label}</div>
//           </div>
//         ))}
//       </div>

//       {/* Table */}
//       <div className="rp-card">
//         <div className="rp-toolbar" style={{ padding: '14px 16px' }}>
//           <div className="rp-chips">
//             {ROLE_FILTERS.map((r) => (
//               <button
//                 key={r.key || 'all'}
//                 className={`rp-chip ${roleFilter === r.key ? 'active' : ''}`}
//                 onClick={() => setRoleFilter(r.key)}
//               >
//                 {r.label}
//               </button>
//             ))}
//           </div>
//           <div className="rp-spacer" />
//           <div className="rp-search">
//             <Search size={14} />
//             <input
//               className="rp-input"
//               placeholder="Search name, mobile, district…"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>
//         </div>

//         {summaryLoading && !execSummary ? (
//           <div className="rp-loading">Loading…</div>
//         ) : people.length === 0 ? (
//           <div className="rp-empty">
//             <b>No managers or executives found</b>
//             Try another date range or clear the search.
//           </div>
//         ) : (
//           <div className="rp-table-wrap">
//             <table className="rp-table">
//               <thead>
//                 <tr>
//                   <th>Person</th>
//                   <th className="rp-num">Reports</th>
//                   <th>Lead split</th>
//                   <th className="rp-num">Customers</th>
//                   <th>Last report</th>
//                   <th />
//                 </tr>
//               </thead>
//               <tbody>
//                 {pageRows.map((p) => {
//                   const empty = p.total === 0;
//                   return (
//                     <tr
//                       key={String(p.executiveId)}
//                       className="clickable"
//                       style={empty ? { opacity: 0.7 } : undefined}
//                       onClick={() => openPerson(p)}
//                     >
//                       <td>
//                         <div className="rp-person">
//                           <div className="rp-avatar">{(p.name || '?').charAt(0).toUpperCase()}</div>
//                           <div style={{ minWidth: 0 }}>
//                             <div className="rp-strong">{p.name || '—'}</div>
//                             <div className="rp-sub">
//                               {ROLE_LABEL[p.role] || p.role}
//                               {p.district ? ` · ${p.district}` : ''}
//                             </div>
//                           </div>
//                         </div>
//                       </td>
//                       <td className="rp-num rp-strong" style={{ fontSize: 18 }}>{p.total}</td>
//                       <td style={{ minWidth: 200 }}>
//                         <div className="rp-bar">
//                           {empty ? (
//                             <div style={{ flex: 1 }} />
//                           ) : (
//                             <>
//                               {p.hot > 0 && <div style={{ flex: p.hot, background: FLOW_META.HOT.color }} />}
//                               {p.warm > 0 && <div style={{ flex: p.warm, background: FLOW_META.WARM.color }} />}
//                               {p.cold > 0 && <div style={{ flex: p.cold, background: FLOW_META.COLD.color }} />}
//                             </>
//                           )}
//                         </div>
//                         <div className="rp-pills" style={{ marginTop: 8 }}>
//                           <Pill flow="HOT" value={p.hot} />
//                           <Pill flow="WARM" value={p.warm} />
//                           <Pill flow="COLD" value={p.cold} />
//                         </div>
//                       </td>
//                       <td className="rp-num">{p.uniqueCustomers}</td>
//                       <td className={empty ? 'rp-muted' : ''}>
//                         {empty ? `No reports (${rangeLabel.toLowerCase()})` : formatDateTime(p.lastSubmittedAt)}
//                       </td>
//                       <td className="rp-num">
//                         <button className="rp-link">View →</button>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {totalPages > 1 && (
//           <div className="rp-pager">
//             <span>
//               {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, people.length)} of {people.length}
//             </span>
//             <div className="rp-pager-btns">
//               <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
//               <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default VisitReportDashboardPage;
